import { z } from 'zod';

import {
  emailSchema,
  uuidSchema,
  dateStringSchema,
  phoneSchema,
  currencyCodeSchema,
  listParamsSchema,
} from './common';

// Enums
export const employmentStatusEnum = z.enum([
  'ACTIVE',
  'INACTIVE',
  'ON_LEAVE',
  'TERMINATED',
  'PENDING_START',
]);

export const employmentTypeEnum = z.enum([
  'FULL_TIME',
  'PART_TIME',
  'CONTRACTOR',
  'INTERN',
  'TEMPORARY',
]);

export const payFrequencyEnum = z.enum([
  'WEEKLY',
  'BI_WEEKLY',
  'SEMI_MONTHLY',
  'MONTHLY',
  'ANNUALLY',
]);

// Create employee
export const createEmployeeSchema = z.object({
  employeeNumber: z.string().max(50).optional(),
  legalFirstName: z.string().min(1, 'First name is required').max(100),
  legalMiddleName: z.string().max(100).optional().nullable(),
  legalLastName: z.string().min(1, 'Last name is required').max(100),
  preferredName: z.string().max(100).optional().nullable(),
  workEmail: emailSchema,
  personalEmail: emailSchema.optional().nullable(),
  workPhone: phoneSchema,
  mobilePhone: phoneSchema,
  hireDate: dateStringSchema,
  jobTitle: z.string().min(1, 'Job title is required').max(200),
  departmentId: uuidSchema.optional().nullable(),
  locationId: uuidSchema.optional().nullable(),
  managerId: uuidSchema.optional().nullable(),
  employmentType: employmentTypeEnum.default('FULL_TIME'),
  baseSalary: z.number().positive().optional().nullable(),
  salaryCurrency: currencyCodeSchema.default('USD'),
  payFrequency: payFrequencyEnum.optional().nullable(),
  hourlyRate: z.number().positive().optional().nullable(),
});

// Update employee
export const updateEmployeeSchema = createEmployeeSchema.partial().extend({
  status: employmentStatusEnum.optional(),
  terminationDate: dateStringSchema.optional().nullable(),
});

// Employee list params
export const employeeListParamsSchema = listParamsSchema.extend({
  status: employmentStatusEnum.optional(),
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  managerId: uuidSchema.optional(),
  employmentType: employmentTypeEnum.optional(),
});

// Employee ID param
export const employeeIdParamSchema = z.object({
  id: uuidSchema,
});

// Bulk operations
export const bulkEmployeeIdsSchema = z.object({
  ids: z.array(uuidSchema).min(1).max(100),
});

// Type exports
export type CreateEmployeeInput = z.infer<typeof createEmployeeSchema>;
export type UpdateEmployeeInput = z.infer<typeof updateEmployeeSchema>;
export type EmployeeListParamsInput = z.infer<typeof employeeListParamsSchema>;
export type EmployeeIdParam = z.infer<typeof employeeIdParamSchema>;
export type BulkEmployeeIds = z.infer<typeof bulkEmployeeIdsSchema>;
